import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import RenderWithTooltip from "../../../../../../../../components/RenderWithTooltip";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
// import ChartTableWrapper from "../../../../../../../../components/app/provider/patient/add-new-patient/ChartTableWrapper";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { usePatientAllergyList } from "../../../../../../../../queries/allergies/use-patient-allergies-list";
import { useArchiveAllergy } from "../../../../../../../../queries/allergies/use-patient-allergy-archive";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getXtenantId,
  roleFilter,
} from "../../../../../../../../utils/utils";
import {
  // SEVERITY_COLOR,
  SEVERITY_RECORD,
} from "../../allergy/allergy-constant";
// import { Patientallergy } from "../../../../../../../services";
import StatusChip from "../../../../../../../../components/StatusChip";
import { PRIMITIVE } from "../../../../../../../../tokens/design-tokens";
import Text from "../../../../../../../../components/Text";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import { Patientallergy } from "../../../../../../../../services";
import UpsertAllergiesDrawer from "../../allergy/UpsertAllergiesDrawer";
import { getStoredRealm } from "@/utils/auth-storage";

const ACTION_MENU_OPTIONS = [
  { value: "edit", label: "Edit" },
  { value: "delete", label: "Delete" },
];

interface AllergiesListProps {
  isInsideVisitNote?: boolean;
}

const AllergiesList = ({ isInsideVisitNote = false }: AllergiesListProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: any };

  const { mutateAsync: archiveAllergy } = useArchiveAllergy();
  const allergiesDisclosure = useDisclosure(false);

  const { data: allergyList, refetch } = usePatientAllergyList({
    patientUuid: patientId,
    pagination,
    gridSorting,
    archive: false,
    xTENANTID: getXtenantId() ?? undefined,
  });

  const deleteAllergyDisclosure = useDeleteDisClosure();
  const { hasReadOnly, hasUpdate, hasDelete } = useRoleController("Clinical");

  const COLUMNS = applyDefaultColumnSettings<Patientallergy>(
    [
      {
        field: "allergy",
        headerName: "Allergy",
        flex: 0.35,
        minWidth: 100,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "reaction",
        headerName: "Reaction",
        flex: 0.35,
        minWidth: 90,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "severity",
        headerName: "Severity",
        flex: 0.25,
        minWidth: 80,
        renderCell: ({ value }) => (
          <Text
            isTabletext
            // style={{ color: SEVERITY_COLOR[value] }}
          >
            {SEVERITY_RECORD[value] || "-"}
          </Text>
        ),
      },
      {
        field: "on_set_date",
        headerName: "Onset Date",
        flex: 0.3,
        minWidth: 90,
        renderCell: ({ row }) =>
          row?.onSetDate ? formatDate(row?.onSetDate) : "-",
      },
      {
        field: "modified",
        headerName: "Updated Date",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ row }) =>
          row?.modifiedDate ? formatDate(row?.modifiedDate) : "-",
      },
      {
        field: "active",
        headerName: "Status",
        flex: 0.2,
        minWidth: 80,
        renderCell: ({ value }) => (
          <StatusChip
            label={value ? "Active" : "Inactive"}
            color={
              value
                ? PRIMITIVE.color.orange[600]
                : PRIMITIVE.color.neutrals[2400]
            }
            bgColor={
              value
                ? PRIMITIVE.color.orange[50]
                : PRIMITIVE.color.neutrals[1000]
            }
          />
        ),
      },
      {
        field: "note",
        headerName: "Note",
        flex: 0.5,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: ACTION_MENU_OPTIONS,
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  // setSelectedAllergy(row as Patientallergy);
                  allergiesDisclosure.open(row);
                } else deleteAllergyDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["severity", "on_set_date", "modified"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveAllergy({
      patientAllergyId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <DeleteModal
        w="380px"
        open={deleteAllergyDisclosure.opened}
        onClose={deleteAllergyDisclosure.close}
        id={deleteAllergyDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this allergy"}
      />
      <UpsertAllergiesDrawer disclosureHost={allergiesDisclosure} />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={allergyList?.data?.content ?? []}
        rowCount={allergyList?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, hasReadOnly || isInsideVisitNote)}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={gridSorting}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default AllergiesList;
